import React, { useRef, useState } from 'react';
import { Dropdown } from 'components/selectors/Dropdown';

export interface MultiSelectOption {
	value: string;
	label: string;
}

interface MultiSelectFilterProps {
	options: MultiSelectOption[];
	/** Currently selected values. Empty means "no filter", never "match nothing". */
	selected: string[];
	onChange: (next: string[]) => void;
	/** Shown on the trigger when nothing is selected. */
	placeholder: string;
	ariaLabel: string;
	title?: string;
}

/**
 * A checkbox popover that reads like the single `<select>`s beside it.
 *
 * A native `<select multiple>` would be fewer lines, but it needs ctrl-click to
 * pick a second value and cannot show a summary when closed — in a table filter
 * row, where the control is one line tall and the current selection has to be
 * legible at a glance, that trades away the two things that matter. So: a
 * trigger that summarises the selection, and a list of real checkboxes.
 *
 * Selection is a *set*, so an empty one means the filter is off. The trigger
 * says "All" in that state rather than "0 selected", because that is what an
 * empty selection does to the query.
 */
export function MultiSelectFilter({ options, selected, onChange, placeholder, ariaLabel, title }: MultiSelectFilterProps) {
	const [open, setOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);

	const toggle = (value: string) => {
		onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
	};

	// One selection reads better as its own name than as "1 selected"; past that,
	// the names stop fitting the cell and a count is the honest summary.
	const summary = !selected.length ? placeholder : selected.length === 1 ? (options.find((o) => o.value === selected[0])?.label ?? selected[0]) : `${selected.length} selected`;

	return (
		<>
			<button
				ref={anchorRef}
				type='button'
				className={`hist-filter-input hist-multi-trigger ${selected.length ? 'active' : ''}`}
				onClick={() => setOpen((v) => !v)}
				aria-label={ariaLabel}
				aria-expanded={open}
				title={selected.length > 1 ? selected.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ') : title}
			>
				<span className='hist-multi-summary'>{summary}</span>
				<span className='hist-multi-caret'>▾</span>
			</button>
			{/* 220, not 190: the engine options lead with a version tag now
			    ("(v1 ⌀HM) BrainerPro Data") and wrapped at the narrower width. */}
			<Dropdown open={open} anchor={anchorRef.current} onClose={() => setOpen(false)} width={220}>
				<div className='hist-multi-list'>
					{options.map((o) => (
						<label key={o.value} className={`hist-multi-item ${selected.includes(o.value) ? 'checked' : ''}`}>
							<input type='checkbox' checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
							<span>{o.label}</span>
						</label>
					))}
				</div>
				{/* Clearing is otherwise N clicks, and "no filter" is the state people
				    return to most often. */}
				{selected.length > 0 && (
					<button type='button' className='hist-multi-clear' onClick={() => onChange([])}>
						Clear selection
					</button>
				)}
			</Dropdown>
		</>
	);
}
